/**
 * TwoShot brand tokens.
 *
 * Single source of truth for colours, fonts, and shape used across:
 *   - server-rendered consent / login-routing pages
 *   - the React login SPA
 *   - transactional email templates
 *
 * Static HTML/CSS keeps matching fallback values for first paint and Android
 * theme-color sampling before JavaScript runs. Runtime/server code should
 * import from here instead of hardcoding the same brand tokens again.
 */

export const colors = {
    // Page background
    bg: "#040404",
    // Card background. Login UI uses #22222299 + backdrop-blur(20px); email
    // clients can't blur, so emails render the equivalent solid colour.
    cardSolid: "#1c1c1c",
    cardTransparent: "#22222299",
    cardBorder: "rgba(255, 255, 255, 0.05)",
    cardShadow: "0 12px 100px 15px rgba(0, 0, 0, 0.5)",

    // Text
    textPrimary: "#ffffff",
    textSecondary: "rgba(255, 255, 255, 0.6)",
    textMuted: "rgba(255, 255, 255, 0.35)",
    // Email-safe equivalents (rgba on text doesn't always render in older clients)
    textSecondaryHex: "#a8a8a8",
    textMutedHex: "#6e6e6e",

    // Brand accent
    accent: "#c21f3c",
    accentActive: "#a11a33",
    accentGlow: "rgba(194, 31, 60, 0.5)",

    // Code/OTP block background (slightly darker than card)
    codeBg: "#0d0d0d",

    // Status
    error: "#ef4444",
    errorBg: "rgba(255, 34, 91, 0.28)",
    errorText: "#FF7C7C",
    success: "#22c55e",
} as const;

export const fonts = {
    // Email clients reliably render system fonts; Poppins is preferred where
    // webfonts work (login SPA, consent page) and acts as a hint otherwise.
    body: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    mono: "ui-monospace, Menlo, Monaco, Consolas, monospace",
} as const;

export const radii = {
    card: "24px",
    field: "8px",
    button: "22px",
} as const;

export const sizes = {
    cardMaxWidth: "520px",
    cardPadding: "40px",
    buttonHeight: "44px",
} as const;
